import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Toast from '../../components/ui/Toast';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import UserForm from './components/UserForm';

const UserManagement = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [toast, setToast] = useState(null);

  // Data state
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Modals
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deactivateModal, setDeactivateModal] = useState({ show: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ show: false, user: null });

  // Check admin access
  useEffect(() => {
    if (userProfile && userProfile?.role !== 'admin') {
      navigate('/admin-login');
    }
  }, [userProfile, navigate]);

  // Load users
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await userService?.getAll();
      setUsers(data);
    } catch (err) {
      setToast({ message: 'Erreur lors du chargement des utilisateurs', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Invite user
  const handleInviteSubmit = async (formData) => {
    try {
      await userService?.inviteUser(formData?.email, formData?.fullName, formData?.role, formData?.password);
      setToast({ message: 'Utilisateur invité avec succès!', type: 'success' });
      setShowUserModal(false);
      loadUsers();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Edit user
  const handleEditSubmit = async (formData) => {
    try {
      await userService?.update(editingUser?.id, {
        fullName: formData?.fullName,
        role: formData?.role,
      });
      setToast({ message: 'Utilisateur modifié avec succès!', type: 'success' });
      setShowUserModal(false);
      setEditingUser(null);
      loadUsers();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Toggle active
  const handleToggleActive = async () => {
    const user = deactivateModal?.user;
    if (!user) return;
    try {
      const newStatus = !user?.isActive;
      await userService?.toggleActive(user?.id, newStatus);
      setToast({
        message: newStatus ? 'Utilisateur réactivé avec succès!' : 'Utilisateur désactivé avec succès!',
        type: 'success',
      });
      setDeactivateModal({ show: false, user: null });
      loadUsers();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    const user = deleteModal?.user;
    if (!user) return;
    try {
      await userService?.deleteUser(user?.id);
      setToast({ message: 'Utilisateur supprimé avec succès!', type: 'success' });
      setDeleteModal({ show: false, user: null });
      loadUsers();
    } catch (err) {
      setToast({ message: 'Erreur: ' + err?.message, type: 'error' });
    }
  };

  // Filter logic
  const filteredUsers = users?.filter((user) => {
    const matchesSearch =
      user?.fullName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = roleFilter === 'all' || user?.role === roleFilter;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && user?.isActive !== false) ||
      (statusFilter === 'inactive' && user?.isActive === false);
    return matchesSearch && matchesRole && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers?.length / itemsPerPage);
  const paginatedUsers = filteredUsers?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const activeCount = users?.filter((u) => u?.isActive !== false)?.length;
  const adminCount = users?.filter((u) => u?.role === 'admin')?.length;
  const managerCount = users?.filter((u) => u?.role === 'manager')?.length;
  const staffCount = users?.filter((u) => u?.role === 'staff')?.length;

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-red-100 text-red-700',
      manager: 'bg-blue-100 text-blue-700',
      staff: 'bg-green-100 text-green-700',
    };
    const labels = {
      admin: 'Admin',
      manager: 'Manager',
      staff: 'Staff',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles?.[role] || 'bg-gray-100 text-gray-700'}`}>
        {labels?.[role] || role}
      </span>
    );
  };

  const getStatusBadge = (isActive) => {
    if (isActive === false) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Inactif
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
        Actif
      </span>
    );
  };

  const PaginationControls = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between mt-6 pt-6 border-t border-border">
        <p className="text-sm text-muted-foreground">
          Page {currentPage} sur {totalPages}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            iconName="ChevronLeft"
          >
            <span className="hidden sm:inline">Précédent</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            iconName="ChevronRight"
            iconPosition="right"
          >
            <span className="hidden sm:inline">Suivant</span>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-24 md:pt-28 pb-8">
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Gestion des Utilisateurs</h1>
            <p className="text-muted-foreground">Inviter, modifier et gérer les accès des utilisateurs</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card rounded-xl shadow-premium border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Users" className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{users?.length}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-xl shadow-premium border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Icon name="UserCheck" className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{activeCount}</p>
                  <p className="text-xs text-muted-foreground">Actifs</p>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-xl shadow-premium border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                  <Icon name="Shield" className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{adminCount}</p>
                  <p className="text-xs text-muted-foreground">Admins</p>
                </div>
              </div>
            </div>
            <div className="bg-card rounded-xl shadow-premium border border-border p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Icon name="Briefcase" className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-foreground">{managerCount + staffCount}</p>
                  <p className="text-xs text-muted-foreground">Managers & Staff</p>
                </div>
              </div>
            </div>
          </div>

          {/* Main Table Card */}
          <div className="bg-card rounded-xl shadow-premium border border-border">
            <div className="p-6 border-b border-border">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                <h2 className="text-2xl font-bold text-foreground">Utilisateurs</h2>
                <Button
                  onClick={() => { setEditingUser(null); setShowUserModal(true); }}
                  iconName="UserPlus"
                  iconPosition="left"
                >
                  Inviter un utilisateur
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Input
                  placeholder="Rechercher par nom ou email..."
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e?.target?.value); setCurrentPage(1); }}
                  iconName="Search"
                />
                <Select
                  value={roleFilter}
                  onChange={(val) => { setRoleFilter(val); setCurrentPage(1); }}
                  options={[
                    { value: 'all', label: 'Tous les rôles' },
                    { value: 'admin', label: 'Administrateur' },
                    { value: 'manager', label: 'Manager' },
                    { value: 'staff', label: 'Staff' },
                  ]}
                />
                <Select
                  value={statusFilter}
                  onChange={(val) => { setStatusFilter(val); setCurrentPage(1); }}
                  options={[
                    { value: 'all', label: 'Tous les statuts' },
                    { value: 'active', label: 'Actif' },
                    { value: 'inactive', label: 'Inactif' },
                  ]}
                />
              </div>
            </div>

            <div className="p-6">
              {loading ? (
                <div className="text-center py-12">
                  <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Chargement des utilisateurs...</p>
                </div>
              ) : filteredUsers?.length === 0 ? (
                <div className="text-center py-12">
                  <Icon name="Users" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground">Aucun utilisateur trouvé</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground">Utilisateur</th>
                          <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden sm:table-cell">Rôle</th>
                          <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden md:table-cell">Statut</th>
                          <th className="text-left py-3 px-3 md:px-4 font-semibold text-foreground hidden lg:table-cell">Inscription</th>
                          <th className="text-right py-3 px-3 md:px-4 font-semibold text-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedUsers?.map((user) => (
                          <tr
                            key={user?.id}
                            className={`border-b border-border hover:bg-muted/50 transition-colors ${user?.isActive === false ? 'opacity-60' : ''}`}
                          >
                            <td className="py-3 px-3 md:px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                  {user?.avatar ? (
                                    <img src={user?.avatar} alt={user?.fullName} className="w-10 h-10 rounded-full object-cover" />
                                  ) : (
                                    <span className="text-sm font-semibold text-primary">
                                      {user?.fullName?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2)}
                                    </span>
                                  )}
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">{user?.fullName}</p>
                                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-3 md:px-4 hidden sm:table-cell">
                              {getRoleBadge(user?.role)}
                            </td>
                            <td className="py-3 px-3 md:px-4 hidden md:table-cell">
                              {getStatusBadge(user?.isActive)}
                            </td>
                            <td className="py-3 px-3 md:px-4 text-sm text-muted-foreground hidden lg:table-cell">
                              {user?.createdAt ? new Date(user?.createdAt)?.toLocaleDateString('fr-FR') : '-'}
                            </td>
                            <td className="py-3 px-3 md:px-4">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditingUser(user); setShowUserModal(true); }}
                                  iconName="Edit"
                                  title="Modifier"
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeactivateModal({ show: true, user })}
                                  iconName={user?.isActive === false ? 'UserCheck' : 'UserX'}
                                  className={user?.isActive === false ? 'text-green-500 hover:text-green-600' : 'text-orange-500 hover:text-orange-600'}
                                  title={user?.isActive === false ? 'Réactiver' : 'Désactiver'}
                                />
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setDeleteModal({ show: true, user })}
                                  iconName="Trash2"
                                  className="text-red-500 hover:text-red-600"
                                  title="Supprimer"
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <PaginationControls />
                </>
              )}
            </div>
          </div>
        </div>

        {/* User Form Modal */}
        {showUserModal && (
          <UserForm
            user={editingUser}
            onClose={() => { setShowUserModal(false); setEditingUser(null); }}
            onSubmit={editingUser ? handleEditSubmit : handleInviteSubmit}
          />
        )}

        {/* Deactivate/Reactivate Confirmation Modal */}
        {deactivateModal?.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-premium border border-border max-w-md w-full">
              <div className="p-4 md:p-6">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full mx-auto mb-4 ${deactivateModal?.user?.isActive === false ? 'bg-green-100' : 'bg-red-100'}`}>
                  <Icon
                    name={deactivateModal?.user?.isActive === false ? 'UserCheck' : 'UserX'}
                    className={`w-6 h-6 ${deactivateModal?.user?.isActive === false ? 'text-green-600' : 'text-red-600'}`}
                  />
                </div>
                <h2 className="text-xl font-bold text-foreground text-center mb-2">
                  {deactivateModal?.user?.isActive === false ? 'Réactiver l\'utilisateur' : 'Désactiver l\'utilisateur'}
                </h2>
                <p className="text-muted-foreground text-center mb-6">
                  {deactivateModal?.user?.isActive === false
                    ? <>Voulez-vous réactiver <span className="font-semibold text-foreground">{deactivateModal?.user?.fullName}</span> ? L'utilisateur pourra à nouveau se connecter.</>
                    : <>Voulez-vous désactiver <span className="font-semibold text-foreground">{deactivateModal?.user?.fullName}</span> ? L'utilisateur ne pourra plus accéder au système.</>
                  }
                </p>
                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setDeactivateModal({ show: false, user: null })} className="flex-1">
                    Annuler
                  </Button>
                  <Button
                    onClick={handleToggleActive}
                    className={`flex-1 ${deactivateModal?.user?.isActive === false ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}
                  >
                    {deactivateModal?.user?.isActive === false ? 'Réactiver' : 'Désactiver'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deleteModal?.show && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-premium border border-border max-w-md w-full">
              <div className="p-4 md:p-6">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto mb-4">
                  <Icon name="AlertTriangle" className="w-6 h-6 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-foreground text-center mb-2">
                  Supprimer l'utilisateur
                </h2>
                <p className="text-muted-foreground text-center mb-6">
                  Êtes-vous sûr de vouloir supprimer définitivement <span className="font-semibold text-foreground">{deleteModal?.user?.fullName}</span> ({deleteModal?.user?.email}) ? Cette action est irréversible.
                </p>
                <div className="flex gap-4">
                  <Button variant="outline" onClick={() => setDeleteModal({ show: false, user: null })} className="flex-1">
                    Annuler
                  </Button>
                  <Button onClick={handleDeleteUser} className="flex-1 bg-red-600 hover:bg-red-700">
                    Supprimer
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {toast && (
          <Toast
            message={toast?.message}
            type={toast?.type}
            onClose={() => setToast(null)}
          />
        )}
      </div>
      <Footer />
    </>
  );
};

export default UserManagement;
